import React, { useLayoutEffect, useRef, useState } from 'react';
import { BarChart3, Table2, MoveHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtSigned, ACTION_SERIES } from '../../lib/slotStatsView';

/*
 * Round 14b: the pieces every Slot Stats chart is built from (dataviz method: thin marks, rounded
 * data-ends, hairline grid, axis titles, a legend for >= 2 series, a hover tooltip whose value leads,
 * and a table twin for every chart). Recharts draws the plots; this file owns the chrome.
 *
 * Direction has ONE encoding on the whole page (review 14b): a small up / down triangle in the
 * gain / loss colour of the change bars, beside a signed number in text ink. Text never wears the
 * data colour.
 */

/** Fallback width when the element cannot be measured (jsdom, first paint). */
export const FALLBACK_WIDTH = 640;

/** [ref, width]: the element's content width, kept current by a ResizeObserver. */
export function useElementWidth(fallback = FALLBACK_WIDTH) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.floor(el.getBoundingClientRect().width);
      setWidth((prev) => (prev === w ? prev : w));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width > 0 ? width : fallback];
}

/** The one data series colour (a single series is slot 1; its title names it, so it needs no legend). */
export const SERIES_COLOR = 'var(--viz-1)';

/**
 * Each action type's colour, fixed per type in METRICS order (stats.css --viz-act-N, validated as a
 * stack in that order): hiding a type never repaints the others, and a type's tab charts it in the
 * same colour as its segment.
 */
const ACTION_KEYS = ACTION_SERIES.map((s) => s.key);
export const actionColor = (key) => {
  const i = ACTION_KEYS.indexOf(key);
  return i < 0 ? SERIES_COLOR : `var(--viz-act-${i + 1})`;
};

/**
 * Width the y axis needs for its longest tick label (11px tabular figures, ~7px each). Recharts
 * places the label tickSize (6) + tickMargin (6) left of the axis line, so 12px more than the text.
 */
export function yAxisWidth(ticks, fmt) {
  const longest = ticks.reduce((n, t) => Math.max(n, String(fmt(t)).length), 1);
  return Math.max(32, Math.ceil(longest * 7) + 16);
}

export const AXIS_TICK = { fill: 'var(--viz-ink-3)', fontSize: 11 };

/**
 * A plot with titled axes: the y title reads up the left side, the x title sits under the axis.
 * `children(width)` draws the chart at the measured plot width.
 */
export function AxisFrame({ yTitle, xTitle = 'Date', children, testid }) {
  const [ref, width] = useElementWidth();
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-1" data-testid={testid}>
      <div className="flex items-center justify-center pb-5" aria-hidden>
        <span className="viz-ytitle text-[11px] font-medium text-muted-foreground" data-testid={testid && `${testid}-ytitle`}>
          {yTitle}
        </span>
      </div>
      <div ref={ref} className="viz-plot min-w-0">{children(width)}</div>
      <div />
      <div className="text-center text-[11px] font-medium text-muted-foreground" aria-hidden
        data-testid={testid && `${testid}-xtitle`}>
        {xTitle}
      </div>
    </div>
  );
}

/**
 * The legend: a swatch that mirrors the mark (rect for bars and areas, a short line for lines, a
 * dashed line for an estimate) beside a label in TEXT ink - identity never rides on coloured text.
 */
export function Legend({ items, className, testid }) {
  if (!items || !items.length) return null;
  return (
    <ul className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground', className)}
      aria-label="Legend" data-testid={testid}>
      {items.map((it) => (
        <li key={it.key} className="inline-flex items-center gap-1.5" data-testid={testid && `${testid}-${it.key}`}>
          {it.kind === 'line' ? (
            <span className="inline-block h-0.5 w-3.5 rounded-full" style={{ background: it.color }} aria-hidden />
          ) : it.kind === 'dash' ? (
            <span className="inline-block w-3.5 border-t-2 border-dashed" style={{ borderColor: it.color }} aria-hidden />
          ) : it.kind === 'faint' ? (
            <span className="inline-block h-2.5 w-2.5 rounded-[3px]" aria-hidden
              style={{ border: `1.5px solid ${it.color}`, background: `color-mix(in srgb, ${it.color} 22%, transparent)` }} />
          ) : (
            <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: it.color }} aria-hidden />
          )}
          <span>{it.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** The direction mark: an up / down triangle in the gain / loss colour, a short grey dash for no change. */
export function DeltaMark({ value, className }) {
  const dir = typeof value !== 'number' || !Number.isFinite(value) || Math.round(value * 10) === 0 ? 0 : (value > 0 ? 1 : -1);
  if (dir === 0) {
    return <span className={cn('inline-block h-0.5 w-2 shrink-0 rounded-full bg-[color:var(--viz-deemph)]', className)} aria-hidden />;
  }
  return (
    <svg width="8" height="7" viewBox="0 0 8 7" aria-hidden className={cn('shrink-0', className)}
      data-dir={dir > 0 ? 'up' : 'down'}>
      <path d={dir > 0 ? 'M4 0.2 L7.8 6.8 L0.2 6.8 Z' : 'M0.2 0.2 L7.8 0.2 L4 6.8 Z'}
        fill={dir > 0 ? 'var(--viz-gain)' : 'var(--viz-loss)'} strokeLinejoin="round" />
    </svg>
  );
}

/** A signed change in text ink with its direction mark: "▲ +975". `text` overrides the number. */
export function Delta({ value, text, className, testid }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} data-testid={testid}>
      <DeltaMark value={value} />
      <span>{text !== undefined ? text : fmtSigned(value)}</span>
    </span>
  );
}

/**
 * The tooltip box: the day on top, then one row per series with the VALUE strong and the label
 * secondary, keyed by a short line of the series colour (or a direction mark). A `muted` row (a
 * hidden or unrecorded type) fades to 70%: its secondary label still clears 4.5:1 on the popover
 * (4.9:1; 55% measured 3.5:1).
 */
export function TooltipBox({ title, rows, footer, testid = 'viz-tooltip' }) {
  return (
    <div className="min-w-[10rem] max-w-[17rem] rounded-xl border border-white/10 bg-popover px-3 py-2 text-xs shadow-lg"
      role="status" data-testid={testid}>
      <p className="mb-1.5 font-medium text-muted-foreground">{title}</p>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.key}
            className={cn('flex items-center gap-2', r.sub && 'pl-5', r.lit && '-mx-1.5 rounded-md bg-white/[.07] px-1.5', r.muted && 'opacity-70')}
            data-row={r.key}>
            {r.mark ? <span className="inline-flex w-3 shrink-0 justify-center">{r.mark}</span>
              : r.color ? (
                <span className={cn('inline-block w-3 shrink-0 rounded-full', r.lit ? 'h-1' : 'h-0.5')} style={{ background: r.color }} aria-hidden />
              ) : !r.sub && <span className="inline-block w-3 shrink-0" aria-hidden />}
            <span className={cn('tnum text-foreground', r.sub || r.medium ? 'font-medium' : 'font-semibold')}>{r.value}</span>
            <span className={cn('truncate', r.lit ? 'text-foreground' : 'text-muted-foreground')}>{r.label}</span>
          </li>
        ))}
      </ul>
      {footer && <div className="mt-1.5 space-y-1 border-t border-white/10 pt-1.5 text-muted-foreground">{footer}</div>}
    </div>
  );
}

/**
 * A bar path with a 4px rounded DATA END and a square base: the top for a value >= 0, the bottom
 * for a negative one. (x, y, w, h) is the rect; h may arrive negative for negative bars.
 */
export function barPath(x, y, w, h, end = 'top', r = 4) {
  const top = Math.min(y, y + h);
  const height = Math.abs(h);
  if (!(w > 0) || !(height > 0)) return '';
  const rr = Math.max(0, Math.min(r, w / 2, height));
  const L = x;
  const R = x + w;
  const T = top;
  const B = top + height;
  if (end === 'bottom') {
    return `M${L},${T}H${R}V${B - rr}Q${R},${B} ${R - rr},${B}H${L + rr}Q${L},${B} ${L},${B - rr}Z`;
  }
  return `M${L},${B}V${T + rr}Q${L},${T} ${L + rr},${T}H${R - rr}Q${R},${T} ${R},${T + rr}V${B}Z`;
}

/**
 * The part of a column with a rounded top end - its top at T, corner radius rr, the corners the
 * quadratic curves barPath draws - that lies between y = a and y = b (a < b): what one segment of
 * a stacked bar draws. Every segment inside the top rr px follows the column's rounded end, not
 * only the topmost one: a 1-post sliver on top would otherwise carry a sub-pixel rounding and
 * leave the column's corners square. Below the rounded end it is a plain rectangle.
 */
export function columnBandPath(x, w, T, rr, a, b) {
  if (!(w > 0) || !(b > a)) return '';
  const L = x;
  const R = x + w;
  if (!(rr > 0) || a >= T + rr) return `M${L},${a}H${R}V${b}H${L}Z`;
  // the left corner P0 (L, T+rr) -> P1 (L, T) -> P2 (L+rr, T): at s, x = L + rr s^2, y = T + rr (1-s)^2
  const sAt = (y) => 1 - Math.sqrt(Math.min(1, Math.max(0, (y - T) / rr)));
  const pt = (s) => [L + rr * s * s, T + rr * (1 - s) * (1 - s)];
  const s0 = sAt(b);                       // the lower cut (0: the band reaches below the corner)
  const s1 = sAt(a);                       // the upper cut
  const [x0, y0] = pt(s0);
  const [x1, y1] = pt(s1);
  // the curve between s0 and s1: control point B(s0) + (s1 - s0)((1 - s0)(P1 - P0) + s0 (P2 - P1))
  const cx = x0 + (s1 - s0) * s0 * rr;
  const cy = y0 - (s1 - s0) * (1 - s0) * rr;
  const m = (px) => L + R - px;            // mirrored onto the right corner
  const side = b > y0;                     // a straight side below the corner
  return `M${x0},${b}${side ? `V${y0}` : ''}Q${cx},${cy} ${x1},${y1}H${m(x1)}`
    + `Q${m(cx)},${cy} ${m(x0)},${y0}${side ? `V${b}` : ''}Z`;
}

/*
 * The action bars' shades (owner, 2026-09-29: "too dull ... don't give that buffer, within 1 bar").
 * Every shade is OPAQUE - a share of the type's colour over the chart surface, or the colour lifted
 * toward white - so the segments of a stacked bar can touch (and overlap by SEAM_PX) with no
 * surface showing between them, in every state:
 *   normal           the colour itself (no 0.9 opacity: it dulled the palette)
 *   hovered column   lifted ACTIVE_LIFT% toward white
 *   highlighted type lifted LIT_LIFT% toward white
 *   the other types  DIM_SHARE of the colour over the surface
 *   incomplete bar   (today so far, a short week) the colour lifted PARTIAL_LIFT% toward white: a
 *                    LIGHTER solid fill, still bright (a share over the dark surface read muddy),
 *                    no outline; lifted further when hovered / highlighted, dimmed like the rest
 */
export const ACTIVE_LIFT = 8;
export const LIT_LIFT = 16;
export const DIM_SHARE = 0.5;
export const PARTIAL_LIFT = 30;

/** `share` of `color` over the chart surface, as an opaque colour. */
export const onSurface = (color, share) => (share >= 1 ? color
  : `color-mix(in srgb, ${color} ${Math.round(share * 100)}%, var(--viz-surface))`);
/** `color` lifted `pct`% toward white. */
export const lifted = (color, pct) => (pct > 0 ? `color-mix(in srgb, ${color} ${100 - pct}%, #fff)` : color);

/** The fill of an action bar or segment in its state: { partial, lit, dim, active } (see above). */
export function actionFill(color, { partial = false, lit = false, dim = false, active = false } = {}) {
  const base = partial ? PARTIAL_LIFT : 0;
  if (dim) return onSurface(lifted(color, base), DIM_SHARE);
  if (lit) return lifted(color, base + LIT_LIFT);
  return lifted(color, base + (active ? ACTIVE_LIFT : 0));
}

/**
 * Recharts `shape` for a single-series bar: rounded data end, the fill from `colorOf(value)` when
 * given (gain / loss), lightened when hovered, and OUTLINED over a faint wash when the bar is
 * incomplete (a day in progress, a week with days missing: payload.partial).
 * `vivid` (one action type charted alone): the action bars' shades instead - full colour, a lift
 * when hovered, and a lighter solid fill with no outline when incomplete, as in the stacked chart.
 */
export function DataEndBar(props) {
  const { x, y, width, height, fill, value, colorOf, isActive, payload, vivid } = props;
  const v = Array.isArray(value) ? value[1] - value[0] : value;
  if (v === null || v === undefined || v === 0) return null;
  const d = barPath(x, y, width, height, v < 0 ? 'bottom' : 'top');
  if (!d) return null;
  const color = colorOf ? colorOf(v) : fill;
  const partial = !!(payload && payload.partial);
  if (vivid) {
    return (
      <path d={d} fill={actionFill(color, { partial, active: !!isActive })}
        className={partial ? 'viz-bar viz-bar-partial' : 'viz-bar'} />
    );
  }
  if (partial) {
    // incomplete: an outline in the full colour (it keeps the 3:1 contrast) around a faint wash
    return (
      <path d={d} fill={color} fillOpacity={isActive ? 0.34 : 0.22} stroke={color} strokeWidth={1.5} strokeLinejoin="round"
        className="viz-bar viz-bar-partial" />
    );
  }
  return <path d={d} fill={color} fillOpacity={isActive ? 1 : 0.9} className="viz-bar" />;
}

/** A segment this short (px) or less is widened to it while its type is highlighted, so it can be found. */
export const SLIVER_PX = 3;

/**
 * How far (px) a segment reaches up UNDER the segment above it. The one above paints later (the
 * bars paint bottom up, in METRICS order) and covers it, so the anti-aliased edge between two
 * segments blends colour into colour - no hairline of dark surface shows inside a bar.
 */
export const SEAM_PX = 1;

/** A highlighted sliver's growth [up, down] (px): around its place, or all upward on the baseline. */
const sliverGrowth = (lit, h, bottom) => {
  if (!lit || !(h < SLIVER_PX)) return [0, 0];
  const g = SLIVER_PX - h;
  return bottom ? [g, 0] : [g / 2, g / 2];
};

/**
 * Recharts `shape` for one type's segment of a stacked bar (payload = an actionStack() row):
 *   - NO gap between segments (owner, 2026-09-29: "don't give that buffer, within 1 bar"): each day
 *     is one continuous column of colours; a segment reaches SEAM_PX under the one above so no
 *     dark hairline shows at the joins. The spacing BETWEEN days' bars stays;
 *   - the column's 4px rounded data end at its top, square inside the bar: the topmost drawn
 *     segment (payload.top) carries it, and a segment below it that reaches into the top 4px (a
 *     1-post sliver on top) follows the same curve (columnBandPath), so every column is rounded;
 *   - an incomplete bar (today so far, a short week: payload.partial) takes a lighter solid fill
 *     (actionFill), with no outline inside the bar;
 *   - `hi` (a highlighted type): it lifts toward white and the other types dim (opaque shades, so
 *     the joins stay seamless); a highlighted segment thinner than SLIVER_PX is drawn SLIVER_PX
 *     tall around its place (all upward on the baseline), the segment above giving way, so a
 *     1-post sliver can be found;
 *   - hovering the segment highlights its type (onHi);
 *   - r16m: `keys` is the stack's order, bottom up (default: the action types, METRICS order - the
 *     "All actions" chart passes nothing and draws exactly as before); the follow-back page passes its
 *     source types, so a stack of any series shares the same seamless look.
 */
export function StackSegment(props) {
  const { x, y, width, height, payload, typeKey, color, isActive, hi, onHi, keys = ACTION_KEYS } = props;
  const v = payload && payload.seg ? payload.seg[typeKey] : null;
  if (!v || !(width > 0) || !(Math.abs(height) > 0)) return null;
  const top0 = Math.min(y, y + height);
  const h0 = Math.abs(height);
  const pxPer = h0 / v;                               // the value scale is linear from 0
  // the drawn segments of this bar, bottom up, and this one's neighbours
  const order = keys.filter((k) => payload.seg[k] > 0);
  const i = order.indexOf(typeKey);
  const below = i > 0 ? order[i - 1] : null;
  const above = i >= 0 && i < order.length - 1 ? order[i + 1] : null;
  const lit = hi === typeKey;
  const dim = !!hi && !lit;
  const [up, down] = sliverGrowth(lit, h0, !below);
  // a highlighted sliver just below grows up into this segment: give way
  const trim = below ? sliverGrowth(hi === below, payload.seg[below] * pxPer, i - 1 === 0)[0] : 0;
  const seam = above ? Math.min(SEAM_PX, Math.max(0, payload.seg[above] * pxPer - up)) : 0;
  const t = top0 - up - seam;
  const h = h0 + up + seam + down - trim;
  if (!(h > 0)) return null;
  // the column's rounded end: the top of its topmost drawn segment (grown, when that one is a
  // highlighted sliver) and the radius barPath gives a bar the column's height. Every segment
  // inside it follows the curve, so a thin type on top never leaves the column square-cornered.
  const sum = (keys) => keys.reduce((a, k) => a + payload.seg[k], 0);
  const topKey = order[order.length - 1];
  const topUp = sliverGrowth(hi === topKey, payload.seg[topKey] * pxPer, order.length === 1)[0];
  const colTop = top0 - sum(order.slice(i + 1)) * pxPer - topUp;
  const colBottom = top0 + h0 + sum(order.slice(0, i)) * pxPer;
  const rr = Math.max(0, Math.min(4, width / 2, colBottom - colTop));
  const d = columnBandPath(x, width, colTop, rr, Math.max(t, colTop), t + h);
  if (!d) return null;
  const partial = !!payload.partial;
  const handlers = onHi ? {
    onMouseEnter: () => onHi(typeKey),
    onMouseLeave: () => onHi(null),
  } : {};
  return (
    <path d={d} fill={actionFill(color, { partial, lit, dim, active: !!isActive })}
      className={cn('viz-bar viz-seg', partial && 'viz-bar-partial', lit && 'viz-seg-lit')}
      data-type={typeKey} data-date={payload.date} {...handlers} />
  );
}

/**
 * A horizontally scrolling box that SAYS it scrolls: a fade on the edge that has more, and a
 * "scroll sideways" hint under it while the content is wider than the box (phones).
 * `className` styles the scrolling element itself (e.g. a max height).
 */
export function ScrollX({ children, className, testid }) {
  const ref = useRef(null);
  const [st, setSt] = useState({ over: false, start: true, end: true });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const over = el.scrollWidth > el.clientWidth + 1;
      const start = el.scrollLeft <= 1;
      const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
      setSt((s) => (s.over === over && s.start === start && s.end === end ? s : { over, start, end }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener('scroll', update);
      if (ro) ro.disconnect();
    };
  }, []);
  return (
    <div data-testid={testid}>
      <div className="relative">
        <div ref={ref} className={cn('overflow-x-auto', className)}>{children}</div>
        {st.over && !st.end && (
          <div className="pointer-events-none absolute inset-y-0 right-0 z-[2] w-10 bg-gradient-to-l from-[hsl(var(--card))] to-transparent" aria-hidden />
        )}
        {st.over && !st.start && (
          <div className="pointer-events-none absolute inset-y-0 left-0 z-[2] w-4 bg-gradient-to-r from-[hsl(var(--card)/.6)] to-transparent" aria-hidden />
        )}
      </div>
      {st.over && (
        <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={testid && `${testid}-hint`}>
          <MoveHorizontal className="h-3.5 w-3.5" aria-hidden />
          Scroll sideways for more columns
        </p>
      )}
    </div>
  );
}

/** Chart / Table switch in a card header. */
function ViewSwitch({ view, onChange, testid }) {
  const btn = (v, Icon, label) => (
    <button type="button" onClick={() => onChange(v)} aria-pressed={view === v}
      className={cn(
        'tap inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium transition-colors',
        view === v ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
      data-testid={testid && `${testid}-view-${v}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      <span>{label}</span>
    </button>
  );
  return (
    <div className="inline-flex shrink-0 items-center rounded-full border border-white/10 p-0.5" role="group" aria-label="Show as">
      {btn('chart', BarChart3, 'Chart')}
      {btn('table', Table2, 'Table')}
    </div>
  );
}

/**
 * A chart card: icon + title + one-line subtitle, the Chart/Table switch, an optional control row,
 * the plot (or its table twin), the legend and a footnote.
 */
export function ChartCard({
  icon: Icon, title, subtitle, controls, legend, footer, table, children, testid, className, badge, after,
}) {
  const [view, setView] = useState('chart');
  return (
    <section className={cn('aurora-card min-w-0 p-4 sm:p-5', className)} data-testid={testid} aria-label={title}>
      <header className="mb-3 flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 font-heading text-base font-semibold leading-tight text-foreground">
            {Icon && <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />}
            <span>{title}</span>
            {badge}
          </h2>
          {subtitle && <p className="mt-1 text-xs text-muted-foreground text-pretty" data-testid={testid && `${testid}-subtitle`}>{subtitle}</p>}
        </div>
        {table && <ViewSwitch view={view} onChange={setView} testid={testid} />}
      </header>
      {controls && <div className="mb-4">{controls}</div>}
      {view === 'table' && table ? (
        <div data-testid={testid && `${testid}-table`}>{table}</div>
      ) : (
        <>
          {children}
          {legend && <div className="mt-2 pl-5">{legend}</div>}
        </>
      )}
      {after}
      {footer && <div className="mt-3 space-y-1 text-xs text-muted-foreground text-pretty" data-testid={testid && `${testid}-footer`}>{footer}</div>}
    </section>
  );
}

/**
 * A chart's table twin: every value the chart draws, readable without hovering. The date column
 * stays put while the numbers scroll sideways on a phone.
 * columns: [{ key, label, align?: 'right', render?: (row) => node }]
 */
export function DataTableView({ caption, columns, rows, testid }) {
  return (
    <ScrollX className="viz-table-scroll rounded-lg border border-white/5" testid={testid && `${testid}-scroll`}>
      <table className="w-full text-sm tnum" data-testid={testid}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            {columns.map((c, i) => (
              <th key={c.key} scope="col"
                className={cn('whitespace-nowrap px-3 py-2 font-medium', c.align === 'right' && 'text-right', i === 0 && 'viz-sticky-col')}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.date || r.key} className="border-t border-white/5">
              {columns.map((c, i) => {
                const Cell = i === 0 ? 'th' : 'td';
                return (
                  <Cell key={c.key} scope={i === 0 ? 'row' : undefined}
                    className={cn('whitespace-nowrap px-3 py-1.5 font-normal', c.align === 'right' && 'text-right',
                      i === 0 && 'viz-sticky-col text-left text-muted-foreground')}>
                    {c.render ? c.render(r) : r[c.key]}
                  </Cell>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollX>
  );
}

/** A segmented control (the time range). */
export function Segmented({ label, options, value, onChange, testidPrefix, size = 'md' }) {
  return (
    <div className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-full border border-white/10 bg-secondary/40 p-0.5"
      role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)} aria-pressed={value === o.value}
          className={cn(
            'tap inline-flex items-center gap-1.5 rounded-full font-medium transition-colors',
            size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3.5 text-xs sm:text-sm',
            value === o.value
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:bg-white/5 hover:text-foreground',
          )}
          data-testid={testidPrefix && `${testidPrefix}-${o.value}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A compact empty panel inside a chart card (instead of an empty plot with a message across it). */
export function EmptyPanel({ children, testid }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[.02] px-4 py-5 text-center text-sm text-muted-foreground text-pretty"
      data-testid={testid}>
      {children}
    </div>
  );
}
